/**
 * PrismaHero — Full-screen hero used on the MediaLabs landing page.
 * Adapted from the Prisma reference (TSX) into our JSX/Tailwind stack.
 * Pure presentation: the actual CTA behaviours are passed in via props
 * so the page (auth-aware) can decide where clicks go.
 */
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Volume2, VolumeX, Play, Pause } from 'lucide-react';
import { useRef, useState } from 'react';

/* ---------------- WordsPullUp ---------------- */
export const WordsPullUp = ({ text, className = '', showAsterisk = false, style }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const words = text.split(' ');

  return (
    <div ref={ref} className={`inline-flex flex-wrap ${className}`} style={style}>
      {words.map((word, i) => {
        const isLast = i === words.length - 1;
        return (
          <motion.span
            key={`${word}-${i}`}
            initial={{ y: 20, opacity: 0 }}
            animate={isInView ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="inline-block relative"
            style={{ marginRight: isLast ? 0 : '0.25em' }}
          >
            {word}
            {showAsterisk && isLast && (
              <span className="absolute top-[0.65em] -right-[0.3em] text-[0.31em]">*</span>
            )}
          </motion.span>
        );
      })}
    </div>
  );
};

/* ---------------- PrismaHero ---------------- */
/**
 * Props:
 *   navItems       {label, href, onClick, active}[]
 *   logo           { src, alt, onClick, videoSrc? } — optional center nav logo
 *                  If videoSrc is provided, an autoplay/loop/muted <video> is
 *                  rendered instead of the static <img> (PNG used as poster
 *                  fallback). Both fill the circle via object-cover.
 *   title          string rendered with the big "pull-up" animation
 *   description    short paragraph
 *   primaryCta     { label, onClick }
 *   secondaryCta   { label, onClick }  (optional)
 *   videoSrc       background video URL
 *   overlay        ReactNode rendered centered between nav and hero content
 */
const PrismaHero = ({
  navItems = [],
  logo,
  title = 'MediaLabs',
  description = '',
  primaryCta,
  secondaryCta,
  videoSrc,
  overlay,
}) => {
  const bgVideoRef = useRef(null);
  const [bgPlaying, setBgPlaying] = useState(true);
  const [bgMuted, setBgMuted] = useState(true);

  const togglePlay = () => {
    const v = bgVideoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setBgPlaying(true); }
    else          { v.pause(); setBgPlaying(false); }
  };
  const toggleMute = () => {
    const v = bgVideoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setBgMuted(v.muted);
    // Some browsers require an explicit play() after unmute due to autoplay policy
    if (!v.muted && v.paused) v.play().catch(() => {});
  };

  return (
    <section className="h-screen w-full" data-testid="landing-hero">
      <div className="relative h-full w-full overflow-hidden rounded-2xl md:rounded-[2rem]">

        {/* Animated gradient + blobs background (works without external assets) */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(70% 65% at 25% 20%, rgba(124, 58, 237, 0.55) 0%, transparent 60%),' +
              'radial-gradient(55% 55% at 80% 25%, rgba(236, 72, 153, 0.45) 0%, transparent 60%),' +
              'radial-gradient(65% 60% at 60% 90%, rgba(16, 185, 129, 0.40) 0%, transparent 55%),' +
              'linear-gradient(180deg, #0a0a0a 0%, #141024 100%)',
          }}
        />

        {/* Background video (optional — renders on top of gradient if loads) */}
        {videoSrc && (
          <video
            ref={bgVideoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 h-full w-full object-cover opacity-80"
            src={videoSrc}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            data-testid="landing-bg-video"
          />
        )}

        {/* Navbar — glassmorphism translúcido + controles de video apilados al lateral derecho */}
        {(navItems.length > 0 || logo) && (
          <nav
            className="absolute left-1/2 top-0 z-30 flex -translate-x-1/2 items-stretch gap-1.5 sm:gap-2"
            data-testid="landing-nav"
          >
            <div
              className="flex items-center gap-4 rounded-b-2xl px-4 py-2 sm:gap-7 md:gap-12 md:rounded-b-3xl md:px-9 lg:gap-16
                         bg-white/[0.025] backdrop-blur-xl backdrop-saturate-150
                         border-x border-b border-white/10
                         shadow-[0_8px_32px_-8px_rgba(0,0,0,0.35)]"
              style={{
                WebkitBackdropFilter: 'blur(20px) saturate(150%)',
                backdropFilter: 'blur(20px) saturate(150%)',
              }}
            >
              {navItems.slice(0, Math.ceil(navItems.length / 2)).map((item) => (
                <NavLink key={item.label} item={item} />
              ))}
              {logo && (
                <button
                  type="button"
                  onClick={logo.onClick}
                  data-testid="landing-nav-logo"
                  className="group relative flex h-14 w-14 items-center justify-center rounded-full transition-transform hover:scale-110 sm:h-16 sm:w-16 md:h-20 md:w-20"
                  aria-label="Inicio"
                >
                  {/* Luminous animated halo — preserved as the user loves it */}
                  <span className="absolute -inset-1 rounded-full bg-[conic-gradient(from_0deg,#7c3aed,#ec4899,#10b981,#3b82f6,#7c3aed)] opacity-75 blur-md transition-opacity group-hover:opacity-100 animate-spin-slow" />
                  {/* Solid black ring background lets the logo's own black edges blend cleanly */}
                  <span className="relative h-12 w-12 overflow-hidden rounded-full bg-black ring-2 ring-white/10 sm:h-14 sm:w-14 md:h-16 md:w-16">
                    {logo.videoSrc ? (
                      <video
                        poster={logo.src}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                        className="h-full w-full object-cover"
                        aria-hidden="true"
                      >
                        {(Array.isArray(logo.videoSrc) ? logo.videoSrc : [logo.videoSrc]).map((s) => (
                          <source
                            key={s.src || s}
                            src={s.src || s}
                            type={s.type || (String(s).endsWith('.webm') ? 'video/webm' : 'video/mp4')}
                          />
                        ))}
                      </video>
                    ) : (
                      <img
                        src={logo.src}
                        alt={logo.alt || 'Studio MediaLabs'}
                        className="h-full w-full object-cover"
                        draggable={false}
                      />
                    )}
                  </span>
                </button>
              )}
              {navItems.slice(Math.ceil(navItems.length / 2)).map((item) => (
                <NavLink key={item.label} item={item} />
              ))}
            </div>

            {/* Background video controls — viñetas mini apiladas, pegadas al lateral derecho del nav */}
            {videoSrc && (
              <div
                className="flex flex-col items-center justify-center gap-1 self-start rounded-b-xl px-1 py-1.5
                           bg-white/[0.025] backdrop-blur-xl backdrop-saturate-150
                           border-x border-b border-white/10
                           shadow-[0_8px_24px_-8px_rgba(0,0,0,0.35)] sm:px-1.5 sm:py-2"
                style={{
                  WebkitBackdropFilter: 'blur(20px) saturate(150%)',
                  backdropFilter: 'blur(20px) saturate(150%)',
                }}
                data-testid="landing-bg-video-controls"
              >
                <button
                  type="button"
                  onClick={togglePlay}
                  aria-label={bgPlaying ? 'Pausar video' : 'Reanudar video'}
                  data-testid="landing-bg-play-toggle"
                  className="flex h-6 w-6 items-center justify-center rounded-full text-white/80 transition-all hover:bg-white/10 hover:text-white sm:h-7 sm:w-7"
                  title={bgPlaying ? 'Pausar' : 'Reanudar'}
                >
                  {bgPlaying ? <Pause size={12} /> : <Play size={12} />}
                </button>
                <button
                  type="button"
                  onClick={toggleMute}
                  aria-label={bgMuted ? 'Activar sonido' : 'Silenciar'}
                  data-testid="landing-bg-mute-toggle"
                  className="flex h-6 w-6 items-center justify-center rounded-full text-white/80 transition-all hover:bg-white/10 hover:text-white sm:h-7 sm:w-7"
                  title={bgMuted ? 'Activar sonido' : 'Silenciar'}
                >
                  {bgMuted ? <VolumeX size={12} /> : <Volume2 size={12} />}
                </button>
              </div>
            )}
          </nav>
        )}

        {/* Overlay slot — centered, positioned just under the nav, above the title */}
        {overlay && (
          <div
            className="pointer-events-none absolute inset-x-0 top-24 z-20 flex justify-center px-4 md:top-28"
            data-testid="landing-overlay-slot"
          >
            <div className="pointer-events-auto w-full max-w-3xl">
              {overlay}
            </div>
          </div>
        )}

        {/* Hero content */}
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-2 sm:px-6 md:px-10">
          <div className="grid grid-cols-12 items-end gap-4">

            {/* Giant title */}
            <div className="col-span-12 lg:col-span-7">
              <h1
                className="font-medium leading-[0.88] tracking-[-0.06em] text-[16vw] sm:text-[14vw] md:text-[12vw] lg:text-[11vw] xl:text-[10vw] 2xl:text-[10vw]"
                style={{ color: '#E1E0CC' }}
              >
                <WordsPullUp text={title} showAsterisk />
              </h1>
            </div>

            {/* Description + CTAs */}
            <div className="col-span-12 flex flex-col gap-5 pb-6 lg:col-span-5 lg:pb-14 lg:pl-6">
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="text-xs sm:text-sm md:text-base"
                style={{ lineHeight: 1.35, color: 'rgba(225, 224, 204, 0.85)' }}
                data-testid="landing-description"
              >
                {description}
              </motion.p>

              <div className="flex flex-wrap items-center gap-3">
                {primaryCta && (
                  <motion.button
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    onClick={primaryCta.onClick}
                    data-testid="landing-cta-primary"
                    className="group inline-flex items-center gap-2 self-start rounded-full py-1 pl-5 pr-1 text-sm font-medium transition-all hover:gap-3 sm:text-base"
                    style={{ background: '#E1E0CC', color: '#0a0a0a' }}
                  >
                    {primaryCta.label}
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-full transition-transform group-hover:scale-110 sm:h-10 sm:w-10"
                      style={{ background: '#0a0a0a' }}
                    >
                      <ArrowRight className="h-4 w-4" style={{ color: '#E1E0CC' }} />
                    </span>
                  </motion.button>
                )}

                {secondaryCta && (
                  <motion.button
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
                    onClick={secondaryCta.onClick}
                    data-testid="landing-cta-secondary"
                    className="inline-flex items-center self-start rounded-full border px-5 py-2 text-sm font-medium backdrop-blur-sm transition-colors sm:text-base"
                    style={{
                      borderColor: 'rgba(225, 224, 204, 0.35)',
                      color: '#E1E0CC',
                      background: 'rgba(255,255,255,0.04)',
                    }}
                  >
                    {secondaryCta.label}
                  </motion.button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- NavLink (internal) ---------------- */
const NavLink = ({ item }) => (
  <a
    href={item.href || '#'}
    onClick={item.onClick}
    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    className={`relative text-[11px] uppercase tracking-[0.14em] transition-colors sm:text-xs md:text-sm ${
      item.active ? 'text-white' : ''
    }`}
    style={{ color: item.active ? '#fff' : 'rgba(225, 224, 204, 0.75)' }}
    onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
    onMouseLeave={(e) => {
      e.currentTarget.style.color = item.active ? '#fff' : 'rgba(225, 224, 204, 0.75)';
    }}
  >
    {item.label}
    {item.active && (
      <span className="absolute -bottom-1 left-0 right-0 h-[2px] rounded-full bg-gradient-to-r from-fuchsia-400 via-violet-400 to-emerald-400" />
    )}
  </a>
);

export { PrismaHero };
export default PrismaHero;
